import { AfterContentInit, Component, EventEmitter, Input, isDevMode, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { EAssetType, EStateAsset, IAsset } from 'src/app/models/products/asset';
import { ProductsService } from 'src/app/services/products.service';
import { environment } from 'src/environments/environment';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IPreference } from 'src/app/models/users/userProfile';
import { AuthService } from 'src/app/services/auth.service';
import { Permission } from 'src/app/constants/permission.constant';
import { AssetUploadDialogComponent } from 'src/app/components/shared/asset-upload-dialog/asset-upload-dialog.component';
import {
  UploadMode,
  UploadAssetType,
  getAllowedExtensions,
  formatAllowedHint,
  validateFileExtension,
} from 'src/app/components/shared/asset-upload-dialog/upload-extensions';

@Component({
    selector: 'app-card-item',
    templateUrl: './card-item.component.html',
    styleUrls: ['./card-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class CardItemComponent implements AfterContentInit, OnInit {
  @Input() showExternalStatus!: boolean;
  @Input() showParentInfo!: boolean;
  @Input() dataType!: any;
  @Input() data!: IAsset;
  @Input() editCallback!: any;
  @Input() touchId: string = '';
  @Input() releasedInCollection: boolean = false;
  // The parent section this card belongs to. Under the formula-grouped shape
  // (refactor/output-assets-view-group-by-formula) a section is one row per
  // OutputFormula containing mixed-media assets — no per-format shape, no
  // legacy stillFormat/videoFormat. Kept loose-typed so card-item can peek at
  // sibling assets (via .assets) without knowing anything else about the
  // section's identity.
  @Input() parentSection: { assets?: IAsset[] } = {};
  @Output() done = new EventEmitter<any>();
  @Output() selectedCheckbox = new EventEmitter();
  isImageLoaded = false;

  EStateAsset = EStateAsset;
  statusMapping: any = {
    [EStateAsset.APPROVED]: 'Approved',
    [EStateAsset.RELEASED]: 'Released',
    [EStateAsset.REJECTED]: 'Rejected',
    [EStateAsset.COMPLETE]: 'Complete',
    [EStateAsset.PROCESSING]: 'Processing',
    [EStateAsset.OUT_DATED]: 'OutDated',
  };
  apiBase = environment.apiBase;
  currentConfigDate!: IPreference;
  Permission = Permission;

  constructor(
    private productsService: ProductsService,
    private clipboard: Clipboard,
    private activeRoute: ActivatedRoute,
    private _alert: MatSnackBar,
    public authService: AuthService,
    private dialog: MatDialog,
  ) {}

  // Drag and drop upload
  hovering: boolean = false;
  uploaded: boolean = false;
  invalidFile: boolean = false;
  invalidReason: string = '';
  newOutputFile!: File;
  newOutputFileImage!: string | ArrayBuffer | null;
  fileDrop(event: any) {
    event.preventDefault();
    event.stopPropagation();
    this.uploaded = false;
    if (event.dataTransfer.files.length != 1) {
      this.invalidReason = 'Upload one file';
      this.invalidFile = true;
      return;
    }
    const file = event.dataTransfer.files[0];
    if (isDevMode()) {
      console.log('[DEV] [CardItemComponent] file dropped', file.name);
    }
    if (this.dataType == 'postProduction' || this.dataType == 'production') {
      const mode: UploadMode = this.dataType == 'postProduction' ? 'post' : 'shoot';
      const result = validateFileExtension(file, getAllowedExtensions(mode, this.assetType));
      if (!result.valid) {
        this.invalidReason = result.error;
        this.invalidFile = true;
        return;
      }
      this.invalidFile = false;
      this.openAssetUploadDialog(mode, file);
      this.hovering = false;
      return;
    }
    if (!this.validateFile(file)) {
      this.invalidReason = 'Invalid File Format';
      this.invalidFile = true;
      return;
    }
    this.invalidFile = false;
    this.newOutputFile = file;
    this.extractImageFromFile();
  }

  private get assetType(): UploadAssetType | undefined {
    return this.data.type === 'Video' ? 'Video' : this.data.type === 'Still' ? 'Still' : undefined;
  }

  get allowedFormatsHint(): string {
    if (this.dataType != 'postProduction' && this.dataType != 'production') return '';
    const mode: UploadMode = this.dataType == 'postProduction' ? 'post' : 'shoot';
    return formatAllowedHint(getAllowedExtensions(mode, this.assetType));
  }

  openAssetUploadDialog(mode: UploadMode, file?: File): void {
    this.hovering = false;
    const dialogData =
      mode === 'shoot'
        ? { mode, assetType: this.assetType, productTouchAssetId: this.data.productTouchAssetId, file }
        : { mode, assetType: this.assetType, shootAssetId: this.data.shootAssetId, file };
    const dialogRef = this.dialog.open(AssetUploadDialogComponent, {
      width: '500px',
      data: dialogData,
    });
    dialogRef.afterClosed().subscribe((success) => {
      if (success) {
        const label = mode === 'shoot' ? 'Shoot asset upload complete' : 'Post asset upload complete';
        this._alert.open(label, 'close', {
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          duration: 3000,
          panelClass: ['success'],
        });
      }
    });
  }
  fileUpload(fileInputEvent: FileList) {
    this.newOutputFile = fileInputEvent[0];
    this.extractImageFromFile();
  }
  validateFile(file: File): boolean {
    switch (this.assetExtension) {
      case 'jpg':
      case 'jpeg':
        return file.type == 'image/jpg' || file.type == 'image/jpeg';
      case 'png':
        return file.type == 'image/png';
      case 'tiff':
        return file.type == 'image/tiff';
      case 'webp':
        return file.type == 'image/webp';
      case 'mp4':
        return file.type == 'video/mp4';
      case 'webm':
        return file.type == 'video/webm';
      case 'gif':
        return file.type == 'image/gif';
      default:
        return false;
    }
  }
  extractImageFromFile(): void {
    let fileReader: FileReader = new FileReader();
    fileReader.readAsDataURL(this.newOutputFile!);
    fileReader.addEventListener(
      'load',
      () => {
        this.newOutputFileImage = fileReader.result;
        this.uploaded = true;
      },
      false,
    );
  }
  counter = 0;
  dragEnter() {
    this.counter++;
  }
  dragLeave() {
    this.counter--;
    if (this.counter === 0) {
      this.hovering = false;
    }
  }
  cancel() {
    this.hovering = false;
    this.uploaded = false;
  }
  confirm() {
    this.productsService
      .uploadOutputAsset(this.data.postAssetId!, this.data.outputFormatId!, this.newOutputFile)
      .subscribe((res) => {
        this.hovering = false;
        this.uploaded = false;
        this.data.id = res.id;
        this.data.rawUri = this.data.reviewUri = this.data.thumbnailUri = res.uri!;
        this.data.manuallyUploaded = true;
        this.data.createdAt = this.data.postProcessedDate = res.createdAt;
        this.data.internalReviewState = EStateAsset.APPROVED;
        this._alert.open('Successfully Uploaded Output Asset', 'close', {
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          duration: 2000,
          panelClass: ['success'],
        });
      });
  }
  // File extension derived from the asset's rawUri (e.g. ".jpg", ".mp4").
  // Kept as a method — not a getter — so existing template calls
  // `{{ getFormat().toLowerCase() }}` etc. stay untouched.
  getFormat(): string {
    const ext = this.assetExtension;
    return ext.length > 0 ? `.${ext}` : '';
  }

  // Lowercase extension parsed from data.rawUri (strips any query string and
  // the leading dot). Empty when rawUri is absent or has no extension.
  private get assetExtension(): string {
    const uri = this.data?.rawUri ?? '';
    if (uri.length === 0) return '';
    const withoutQuery = uri.split('?', 2)[0];
    const lastDot = withoutQuery.lastIndexOf('.');
    if (lastDot < 0 || lastDot === withoutQuery.length - 1) return '';
    return withoutQuery.slice(lastDot + 1).toLowerCase();
  }

  copyToClipboard(text: string) {
    this.clipboard.copy(text);
    this._alert.open(`Copied Key: ${text}`, '', {
      horizontalPosition: 'right',
      verticalPosition: 'bottom',
      duration: 2000,
      panelClass: ['info'],
    });
  }

  get parentLabel() {
    return this.data.label?.split('.')[0];
  }

  get outputAssetsSecondary(): string | null {
    if (this.dataType !== 'outputAssets') return null;
    const kind = this.data.outputFormulaKind;
    if (kind === 'Ev') return `Per-select ${this.perSelectOrdinal}`;
    return null;
  }

  get outputAssetsEvChip(): string | null {
    if (this.dataType !== 'outputAssets') return null;
    if (this.data.outputFormulaKind !== 'Ev') return null;
    return `#${this.perSelectOrdinal}`;
  }

  private get perSelectOrdinal(): number {
    return Math.floor((this.data.selectsIndex ?? 0) / 1000) + 1;
  }

  invalidFormat: boolean = false;
  ngOnInit(): void {
    if (
      this.dataType == 'outputAssets' &&
      this.data.type == 'Still' &&
      this.assetExtension == 'tiff'
    ) {
      this.invalidFormat = true;
    }
    this.authService.currentPreference.subscribe((res) => {
      this.currentConfigDate = res;
    });
    window.addEventListener(
      'dragover',
      function (e) {
        e = e || event;
        e.preventDefault();
      },
      false,
    );
    window.addEventListener(
      'drop',
      function (e) {
        e = e || event;
        e.preventDefault();
      },
      false,
    );
  }

  ngAfterContentInit(): void {
    this.done.emit();
  }

  onEdit() {
    if (!this.editCallback) return;
    if (this.dataType == 'outputAssets') {
      if (!this.data.id) return;
      this.editCallback.call(this, {
        id: this.data.id,
        assetType: EAssetType.OUTPUT_ASSETS,
      });
      return;
    }
    this.editCallback.call(this, {
      id: this.data.productTouchAssetId,
      assetType:
        this.dataType == 'production'
          ? EAssetType.SHOOT_ASSETS
          : this.dataType == 'postProduction' && this.data.automatedAsset
            ? EAssetType.AUTO_POST_ASSETS
            : this.dataType == 'postProduction' && !this.data.automatedAsset
              ? EAssetType.POST_ASSETS
              : EAssetType.OUTPUT_ASSETS,
      masterSuiteTouchAssetId: this.data.masterSuiteTouchAssetId,
      outputFormatId: this.data.outputFormatId,
    });
  }

  checkImageLoaded() {
    this.isImageLoaded = true;
  }

  onSelect() {
    this.selectedCheckbox.emit({ touchId: this.touchId, data: this.data });
  }

  getLinkDownload() {
    const tmp = this.data.rawUri!.split('?')[0].split('/');
    const fileName = tmp[tmp.length - 1].split('.');
    var download = `${fileName[0]}.${fileName[1]}`;
    if (this.dataType == 'postProduction') {
      download = `${fileName[0]}_${this.data.selectsIndex / 1000 + 1}.${fileName[1]}`;
    } else if (this.dataType == 'production' && this.data.enhancementFlag) {
      download = `${fileName[0]}_${this.data.enhancementIndex! / 1000 + 1}.${fileName[1]}`;
    }
    this.productsService.downloadSingleImage(this.data.rawUri!.split('//')[1]).subscribe((res) => {
      const blod: Blob = res.body as Blob;
      const a = document.createElement('a');
      const fileData = window.URL.createObjectURL(blod);
      a.href = fileData;
      a.download = download;
      a.click();
      window.URL.revokeObjectURL(fileData);
    });
  }

  getLinkImage() {
    if (!this.authService.hasPermission(Permission.PostAssetsPreRelease) && !this.releasedInCollection) {
      return 'assets/image-placeholder.jpg';
    }
    return ProductsService.getLinkImage(this.data.thumbnailUri);
  }
}
